import { CircleCheck } from "lucide-react";
import { Skeleton } from "../atoms/index.js";
import { AsyncSection, type AsyncStatus } from "../molecules/index.js";
import type { NotificationItem } from "./notifications.types.js";
import { NotificationRow } from "./NotificationRow.js";

/**
 * The feed region of the notifications panel, in its four states.
 *
 * Extracted from `NotificationsPopover`, as `NotificationRow` was, so that file
 * stays under this package's 200-line soft cap. The load states are
 * `AsyncSection`'s (invariant 8):
 * "You're all caught up" is a claim about the inbox, so it renders only for a
 * feed that settled empty — never for one that failed or is still loading
 * (PB-5337).
 */
export function NotificationFeedBody({
  status,
  items,
  onMarkRead,
  onNavigate,
  onDismiss,
  onRetry,
}: {
  readonly status: AsyncStatus;
  readonly items: NotificationItem[];
  readonly onMarkRead: (id: string) => void;
  readonly onNavigate?: (href: string, item: NotificationItem) => void;
  readonly onDismiss: () => void;
  readonly onRetry?: () => void;
}): JSX.Element {
  return (
    <AsyncSection
      status={status}
      loadingLabel="notifications"
      errorTitle="Couldn't load your notifications"
      {...(onRetry ? { onRetry } : {})}
      skeleton={
        <div className="flex flex-col gap-2 px-3 py-3">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      }
      empty={
        <div className="px-4 py-10 text-center">
          <CircleCheck className="w-6 h-6 text-pb-green-text mx-auto mb-2" />
          <div className="text-sm text-pb-text-secondary">
            You&rsquo;re all caught up.
          </div>
        </div>
      }
    >
      <div className="max-h-90 overflow-y-auto py-1">
        {items.map((n) => (
          <NotificationRow
            key={n.id}
            item={n}
            onMarkRead={onMarkRead}
            {...(onNavigate ? { onNavigate } : {})}
            onDismiss={onDismiss}
          />
        ))}
      </div>
    </AsyncSection>
  );
}
